/**
 * Feature Gates initialization for the TWG Microsite.
 *
 * The microsite renders for both anonymous visitors (the marketing /
 * logged-out home view) and authenticated Atlassian users. Both
 * audiences need `fg(...)` to resolve against real Statsig values so
 * that gated UI evaluates correctly and Statsig records exposure
 * events for non-logged-in users.
 *
 * Strategy:
 *   1. Bootstrap the gate client with anonymous identifiers at app
 *      start (called from `index.tsx` before React mounts).
 *   2. Re-identify it via `FeatureGates.updateUser` once the user
 *      logs in (called from `App.tsx`).
 *
 * The microsite never swaps identities in-session — logout triggers a
 * full page navigation — so we don't try to handle account-switch
 * flows here. If that ever changes, this file is the place to add it.
 */
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	type ClientOptions,
	type Identifiers,
	FeatureGateEnvironment,
	PerimeterType,
} from '@atlaskit/feature-gate-js-client/types';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags/setBooleanFeatureFlagResolver';

import { initAnalyticsClient } from '@atlassian/twg-microsite-experience';
import { getConfig } from '@atlassian/twg-microsite-experience/config';

// https://hello.help.atlassian.cloud/servicedesk/customer/portal/3450/MHD-18131?created=true
const TARGET_APP = 'twg-microsite-bifrost_web';

/**
 * Gates the resolver always reports as `true`, regardless of Statsig
 * state. Previously force-enabled by an inline resolver in
 * `index.tsx`; centralising them here means the Statsig-backed
 * resolver below honours them too, even before the SDK has loaded.
 */
const ALWAYS_ON_GATES = new Set<string>();

const getFeatureGateEnvironment = (): FeatureGateEnvironment =>
	getConfig().env === 'production'
		? FeatureGateEnvironment.Production
		: FeatureGateEnvironment.Staging;

/**
 * `perimeter` is typed as required so the same object can be passed
 * unmodified to `FeatureGates.updateUser`, whose first parameter
 * (`FetcherOptions`) requires a defined perimeter.
 */
const featureGatesOptions: ClientOptions & { perimeter: PerimeterType } = {
	apiKey: getConfig().featureGateApiKey,
	environment: getFeatureGateEnvironment(),
	targetApp: TARGET_APP,
	perimeter: PerimeterType.COMMERCIAL,
};

// In-flight promises act as idempotency guards. Both are stored as
// never-rejecting promises (errors are caught in `runAndLog`) so a
// later caller can safely `await` them regardless of SDK failure.
let initPromise: Promise<void> | null = null;
let identifyPromise: Promise<void> | null = null;
let resolverInstalled = false;

/** Install the `fg(...)` resolver exactly once. */
const installResolver = (): void => {
	if (resolverInstalled) {
		return;
	}
	resolverInstalled = true;
	setBooleanFeatureFlagResolver((gateName) => {
		if (ALWAYS_ON_GATES.has(gateName)) {
			return true;
		}
		// Legitimate use of `FeatureGates.checkGate` — this is the
		// implementation `fg(...)` delegates to. Wrapped in try/catch
		// so a failed SDK init can't crash gate evaluation.
		try {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			return FeatureGates.checkGate(gateName);
		} catch {
			return false;
		}
	});
};

/**
 * Run an SDK call, swallow + log any error, and install the resolver.
 * The resolver is installed even on failure so the app keeps rendering
 * (gates fall back to `false`, ALWAYS_ON_GATES still return `true`).
 */
const runAndLog = async (label: string, op: () => Promise<unknown>): Promise<void> => {
	try {
		await op();
		// eslint-disable-next-line no-console
		console.debug(`[FeatureGates] ${label} complete`);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(`[FeatureGates] ${label} failed:`, error);
	} finally {
		installResolver();
	}
};

/**
 * Bootstrap the gate client with anonymous identifiers. Idempotent.
 * Call from the entry point before React renders so `fg(...)` from
 * the logged-out view always hits a live resolver.
 *
 * Statsig requires at least one supported identifier (a bare `{}`
 * yields a 400 "Must provide at least one Statsig-supported
 * identifier" from the gateway). We reuse the AnalyticsWebClient's
 * anonymous id so anonymous gate evaluations and analytics exposure
 * events bucket against the same stable per-browser id.
 *
 * Installs the `fg(...)` resolver **synchronously** before kicking
 * off the Statsig network roundtrip so that the try/catch fallback to
 * `false` for not-yet-loaded gates takes effect immediately — even
 * during the window between `void initializeFeatureGatesAnonymous()`
 * in `index.tsx` and `FeatureGates.initialize` resolving.
 */
export const initializeFeatureGatesAnonymous = (): Promise<void> => {
	if (!initPromise) {
		installResolver();
		const identifiers: Identifiers = {
			analyticsAnonymousId: initAnalyticsClient().getAnonymousId(),
		};
		initPromise = runAndLog('Anonymous initialization', () =>
			FeatureGates.initialize(featureGatesOptions, identifiers),
		);
	}
	return initPromise;
};

/**
 * Re-identify the gate client with a logged-in user's identifiers.
 * Idempotent: subsequent calls return the in-flight / resolved
 * promise. The microsite never swaps identities in-session, so we
 * don't try to handle account- or org-switch flows.
 *
 * @param accountId - The user's Atlassian account ID for targeting
 * @param orgId - The user's Atlassian org ID (required for org-targeted gates)
 */
export const initializeFeatureGates = (accountId: string, orgId: string): Promise<void> => {
	if (identifyPromise) {
		return identifyPromise;
	}

	const identifiers: Identifiers = {
		analyticsAnonymousId: initAnalyticsClient().getAnonymousId(),
		atlassianAccountId: accountId,
		atlassianOrgId: orgId,
	};

	identifyPromise = runAndLog('Identified initialization', async () => {
		await initializeFeatureGatesAnonymous();
		await FeatureGates.updateUser(featureGatesOptions, identifiers);
	});

	return identifyPromise;
};
